import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Gift, Coins, CheckCircle } from "@phosphor-icons/react";

export default function Rewards() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    const [i, r, w] = await Promise.all([
      api.get("/rewards"), api.get("/rewards/redemptions/me"), api.get("/wallet/me"),
    ]);
    setItems(i.data); setRedemptions(r.data); setWallet(w.data);
  };
  useEffect(() => { load(); }, []);

  const redeem = async (item) => {
    if (!window.confirm(`Redeem "${item.name}" for ${item.cost_credits} credits?`)) return;
    setBusy({ ...busy, [item.item_id]: true });
    try {
      await api.post(`/rewards/${item.item_id}/redeem`);
      toast.success(`Redeemed! We'll follow up on fulfillment.`);
      load();
      refresh && refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy({ ...busy, [item.item_id]: false }); }
  };

  const categories = [...new Set(items.map((i) => i.category))];

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="rewards-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Gift size={26} weight="fill" className="text-[#E1252A]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recognition</div>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-bold">Rewards Store</h1>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your credits</div>
          <div className="text-3xl font-bold font-mono text-[#E1252A]" data-testid="rewards-balance">{wallet?.balance ?? 0}</div>
        </div>
      </div>

      <Tabs defaultValue="catalog" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="catalog" data-testid="rewards-tab-catalog">Catalog</TabsTrigger>
          <TabsTrigger value="mine" data-testid="rewards-tab-mine">My redemptions</TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="pt-4 space-y-6">
          {categories.map((cat) => (
            <section key={cat}>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{cat}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.filter((i) => i.category === cat).map((item) => (
                  <div key={item.item_id} className="border border-border bg-background p-4 space-y-3 flex flex-col" data-testid={`reward-${item.item_id}`}>
                    <div>
                      <h3 className="text-base font-semibold tracking-tight">{item.name}</h3>
                      <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{item.description}</p>
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-border">
                      <div className="flex items-center gap-1.5">
                        <Coins size={14} className="text-[#E1252A]" weight="fill" />
                        <span className="text-lg font-bold font-mono text-[#E1252A]">{item.cost_credits}</span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => redeem(item)}
                        disabled={busy[item.item_id] || (wallet?.balance ?? 0) < item.cost_credits || item.stock === 0}
                        className="rounded-sm bg-foreground text-background"
                        data-testid={`redeem-${item.item_id}`}
                      >
                        {(wallet?.balance ?? 0) < item.cost_credits ? "Not enough" : "Redeem"}
                      </Button>
                    </div>
                    {item.stock !== -1 && item.stock !== undefined && (
                      <div className="text-[10px] uppercase tracking-wider text-zinc-400">Stock: {item.stock}</div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
          {items.length === 0 && (
            <div className="text-center text-sm text-zinc-500 p-12 border border-dashed border-border" data-testid="rewards-empty">
              No rewards in the catalog yet. Ask your admin to add some.
            </div>
          )}
        </TabsContent>

        <TabsContent value="mine" className="pt-4">
          <div className="border border-border bg-background">
            <table className="w-full text-sm" data-testid="my-redemptions-table">
              <thead className="border-b border-border"><tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <th className="text-left py-2 px-3">When</th>
                <th className="text-left py-2 px-3">Item</th>
                <th className="text-right py-2 px-3">Cost</th>
                <th className="text-left py-2 px-3">Status</th>
              </tr></thead>
              <tbody>
                {redemptions.map((r) => (
                  <tr key={r.redemption_id} className="border-b border-border" data-testid={`redemption-${r.redemption_id}`}>
                    <td className="py-2 px-3 text-zinc-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3">{r.item_name}</td>
                    <td className="py-2 px-3 text-right font-mono text-[#E1252A]">{r.cost_credits}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className="rounded-sm uppercase text-[10px]">{r.status}</Badge></td>
                  </tr>
                ))}
                {redemptions.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-sm text-zinc-500">You haven't redeemed anything yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
